import React from 'react';
import { TableProps } from '@components-shared/table/types';
import { keyMap } from '@locales/lokalise/keyMap';
import { getCurrentDayUtc } from '@utils/library/dateTime';
import { ENUM_CONSTANTS } from 'components/shared/enum.constants';
import { FormattedDateTimeWithoutOffset } from 'components/shared/formattedDateTime';
import Table from 'components/shared/table/index';
import { getFormattedNumber } from 'utils/helper';
import { IClosedTradesRow } from '../types';

interface IClosedTradesProps {
  api: string;
  exportAPI: string;
  tradingServerFilterValuesUrl: string;
}

const COLUMNS = [
  {
    key: 'login',
    name: keyMap.partnershipIbInfoStatisticsClosetTradersReportAccountNumber,
  },
  {
    key: 'accountType',
    name: keyMap.partnershipIbInfoStatisticsClosetTradersReportAccountType,
  },
  {
    key: 'balance',
    name: keyMap.commonBalance,
    render: (row: IClosedTradesRow, value: number) => getFormattedNumber(value, { currency: row.currency }),
  },
  {
    key: 'ticket',
    name: keyMap.partnershipIbInfoStatisticsClosetTradersReportTicket,
  },
  {
    key: 'side',
    name: keyMap.partnershipIbInfoStatisticsClosetTradersReportSide,
  },
  {
    key: 'symbol',
    name: keyMap.tradingFieldsSymbol,
  },
  {
    key: 'openTime',
    name: keyMap.partnershipIbInfoStatisticsClosetTradersReportOpenTime,
    render: (row: IClosedTradesRow, value: string) => {
      return <FormattedDateTimeWithoutOffset value={value} />;
    },
  },
  {
    key: 'closeTime',
    name: keyMap.partnershipIbInfoStatisticsClosetTradersReportCloseTime,
    render: (row: IClosedTradesRow, value: string) => {
      return <FormattedDateTimeWithoutOffset value={value} />;
    },
  },
  {
    key: 'openPrice',
    name: keyMap.sharedFieldsOpenPrice,
    render: (row: IClosedTradesRow, value: number) => getFormattedNumber(value, { precision: 5 }),
  },
  {
    key: 'closePrice',
    name: keyMap.partnershipIbInfoStatisticsClosetTradersReportClosePrice,
    render: (row: IClosedTradesRow, value: number) => getFormattedNumber(value, { precision: 5 }),
  },
  {
    key: 'difference',
    name: keyMap.partnershipIbInfoStatisticsClosetTradersReportDifference,
    render: (row: IClosedTradesRow, value: number) => getFormattedNumber(value, { precision: 5 }),
  },
  {
    key: 'volume',
    name: keyMap.partnershipIbInfoStatisticsClosetTradersReportVolume,
    render: (row: IClosedTradesRow, value: number) => getFormattedNumber(value, { precision: 2 }),
  },
  {
    key: 'profit',
    name: keyMap.partnershipIbInfoStatisticsClosetTradersReportProfit,
    render: (row: IClosedTradesRow, value: number) => getFormattedNumber(value, { precision: 4 }),
  },
  {
    key: 'rebateInTaCurrency',
    name: keyMap.partnershipIbInfoStatisticsClosetTradersReportRebateInTaCurrency,
  },
  {
    key: 'commission',
    name: keyMap.partnershipIbInfoStatisticsClosetTradersReportCommission,
    render: (row: IClosedTradesRow, value: number) => getFormattedNumber(value, { precision: true }),
  },
  {
    key: 'commissionUnit',
    name: keyMap.partnershipIbInfoStatisticsClosetTradersReportCommissionUnit,
  },
  {
    key: 'commissionInTaCurrency',
    name: keyMap.partnershipIbInfoStatisticsClosetTradersReportCommissionInTaCurrency,
  },
  {
    key: 'spread',
    name: keyMap.partnershipIbInfoStatisticsClosetTradersReportSpread,
    render: (row: IClosedTradesRow, value: number) => getFormattedNumber(value, { precision: true }),
  },
  {
    key: 'spreadUnit',
    name: keyMap.partnershipIbInfoStatisticsClosetTradersReportSpreadUnit,
  },
  {
    key: 'spreadInTaCurrency',
    name: keyMap.partnershipIbInfoStatisticsClosetTradersReportSpreadInTaCurrency,
  },
  {
    key: 'totalInTaCurrency',
    name: keyMap.partnershipIbInfoStatisticsClosetTradersReportTotalInTaCurrency,
  },
  {
    key: 'totalInUsd',
    name: keyMap.partnershipIbInfoStatisticsClosetTradersReportTotalInUsd,
  },
];

const getFilters = (tradingServerFilterValuesUrl: string) => [
  {
    name: 'closeTime',
    title: keyMap.partnershipIbInfoStatisticsClosetTradersReportPeriod,
    type: 'dateTimeRange',
    defaultValue: getCurrentDayUtc(),
    isServerTime: true,
  },
  {
    name: 'metaServerId',
    type: 'dynamicDropDown',
    title: keyMap.sharedFieldsTradingServer,
    api: tradingServerFilterValuesUrl,
  },
  {
    name: 'side',
    title: keyMap.partnershipIbInfoStatisticsClosetTradersReportSide,
    type: 'select',
    options: ENUM_CONSTANTS.CLOSED_TRADES_SIDE,
  },
  {
    name: 'type',
    title: keyMap.sharedFieldsType,
    type: 'select',
    options: ENUM_CONSTANTS.CLOSED_TRADES_TYPE,
    timeFormat: false,
  },
  {
    name: 'login',
    title: keyMap.sharedFieldsTradingAccount,
    type: 'number',
  },
  {
    name: 'ticket',
    title: keyMap.partnershipIbInfoStatisticsClosetTradersReportTicket,
    type: 'number',
  },
  {
    name: 'symbol',
    title: keyMap.tradingFieldsSymbol,
  },
  {
    name: 'profit',
    title: keyMap.partnershipIbInfoStatisticsClosetTradersReportProfit,
    type: 'numberRange',
  },
];

export const ClosedTradesReport = ({
  api,
  exportAPI,
  tradingServerFilterValuesUrl,
}: IClosedTradesProps): JSX.Element => {
  const filters = getFilters(tradingServerFilterValuesUrl);

  return (
    <Table
      tableId="closed-trades-report"
      columns={COLUMNS as TableProps['columns']}
      filterFields={filters as TableProps['filterFields']}
      apiExplicitRequest={true}
      api={api}
      exportAPI={exportAPI}
    />
  );
};
